import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, ChangeDetectorRef, input, model, effect, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TableDefinition, TableItem } from './table-definition.interface';
import { TableType } from './table-type.enum';

@Component({
    selector: 'ca-table',
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({ height: '0', opacity: 0, overflow: 'hidden' })),
            state('expanded', style({ height: '*', opacity: 1, overflow: 'visible' })),
            transition('collapsed <=> expanded', [animate('200ms ease-in-out')])
        ])
    ]
})
export class TableComponent<T extends TableItem> {
    private fb = inject(FormBuilder);
    private cdr = inject(ChangeDetectorRef);

    public headerColor = input<string>('var(--ca-grey-98)');
    public withPadding = input<boolean>(false);
    public centerItems = input<boolean>(false);

    public definitions = input.required<TableDefinition[]>();
    public items = model.required<T[]>();
    public selectedItem?: T;
    public editForm: FormGroup;
    public readonly tableType = TableType;
    public expandedItems: { [key: string]: boolean } = {};

    private parentOfEditedDefinition: TableDefinition | null;
    private editedDefinition: TableDefinition | null;
    private originalValues: { [key: string]: any } = {};

    constructor() {
        this.editForm = this.fb.group({});
        effect(() => {
            this.setSelectedItem(this.items()?.[0]);
        });
    }

    public setSelectedItem(item: T) {
        this.selectedItem = item;
        this.cdr.detectChanges();
    }

    public getFormControl(field: string): FormControl {
        return this.editForm.get(field) as FormControl;
    }

    public get hasEditableDefinition(): boolean {
        return this.definitions()?.some(definition => definition.editable);
    }

    public get hasChildren(): boolean {
        return this.definitions()?.some(definition => definition.children);
    }

    public onEdit(parentDefinition: TableDefinition, childDefinition?: TableDefinition) {
        this.onCancel();
        this.parentOfEditedDefinition = childDefinition ? parentDefinition : null;
        this.editedDefinition = childDefinition ? childDefinition : parentDefinition;

        this.items().forEach(item => {
            const itemField = childDefinition
                ? item[parentDefinition.field]?.children?.[childDefinition.field]
                : item[parentDefinition.field];

            this.originalValues[item.id] = itemField?.value;
            this.editForm.addControl(
                item.id,
                new FormControl({ value: itemField?.value || '', disabled: itemField?.disabled }, [Validators.min(0)])
            );
        });

        this.cdr.markForCheck();
    }

    public onSave() {
        if (this.editForm.invalid) return;
        if (!this.editedDefinition) return;

        let hasChanges = false;
        const definition = this.editedDefinition;
        const editedItems = this.items().map(item => {
            const control = this.editForm.get(item.id);
            if (control?.dirty) {
                hasChanges = true;

                const parentDefinition = this.parentOfEditedDefinition;

                if (parentDefinition) {
                    // if parentDefinition is set, edited definition is a child
                    return {
                        ...item,
                        [parentDefinition.field]: {
                            ...item[parentDefinition.field],
                            children: {
                                ...item[parentDefinition.field]?.children,
                                [definition.field]: {
                                    ...item[parentDefinition.field]?.children?.[definition.field],
                                    value: control.value
                                }
                            }
                        }
                    };
                } else {
                    // if parentDefinition is not set, edited definition is a parent
                    return {
                        ...item,
                        [definition.field]: {
                            ...item[definition.field],
                            value: control.value
                        }
                    };
                }
            }
            return item;
        });

        if (hasChanges) {
            this.items.set(editedItems);
            this.selectedItem = editedItems[0];
        }

        this.resetEditState();
    }

    public onCancel() {
        if (!this.editedDefinition) return;

        const definition = this.editedDefinition;
        const parentDefinition = this.parentOfEditedDefinition;

        this.items().forEach(item => {
            if (this.originalValues[item.id] !== undefined) {
                if (parentDefinition) {
                    // if parentDefinition is provided, edited definition is a child
                    item[parentDefinition.field].children[definition.field].value = this.originalValues[item.id];
                } else {
                    // if parentDefinition is not provided, edited definition is a parent
                    item[definition.field].value = this.originalValues[item.id];
                }
            }
        });

        this.resetEditState();
    }

    public isEdited(definition?: TableDefinition): boolean {
        return this.editedDefinition === definition;
    }

    public toggleExpand(definition: TableDefinition): void {
        this.expandedItems[definition.field] = !this.expandedItems[definition.field];
    }

    private resetEditState() {
        this.editForm = this.fb.group({});
        this.originalValues = {};
        this.editedDefinition = null;
    }
}
